import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAssessmentStatus } from '../hooks/useAssessmentStatus';
import { InvestmentTypeModal } from '../components/report/InvestmentTypeModal';
import { cancelAssessment, getAssessmentAnalysis, setInvestmentType } from '../services/api';
import type { AnalysisResponse, AssessmentStatusResponse } from '../types/api';
import { BRAND_CODE, BRAND_NAME } from '../config/branding';

function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s} sec`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return rs === 0 ? `${m} min` : `${m} min ${rs} sec`;
}

interface PipelineStep {
  num: number;
  name: string;
  subtitle?: string;
  status: 'done' | 'running' | 'pending';
  durationSec?: number;
}

function buildPipeline(status: AssessmentStatusResponse): PipelineStep[] {
  const docs = status.documents;
  const totalBytes = 0;  // status doesn't expose per-doc bytes — keep subtitle qualitative
  const docCountStr = `${docs.length} file${docs.length !== 1 ? 's' : ''}`;
  void totalBytes;

  const allDocsDone = (stepName: string): 'done' | 'running' | 'pending' => {
    if (docs.length === 0) return 'pending';
    let anyRunning = false;
    let allDone = true;
    for (const d of docs) {
      const step = d.processing_steps.find((s) => s.step_name === stepName);
      if (!step) { allDone = false; continue; }
      if (step.status === 'running') anyRunning = true;
      if (step.status !== 'done') allDone = false;
    }
    if (allDone) return 'done';
    if (anyRunning) return 'running';
    return 'pending';
  };

  // Sum step duration across docs in seconds
  const sumDuration = (stepName: string): number | undefined => {
    let total = 0;
    let any = false;
    for (const d of docs) {
      const step = d.processing_steps.find((s) => s.step_name === stepName);
      if (step?.started_at && step?.completed_at) {
        any = true;
        total += (new Date(step.completed_at).getTime() - new Date(step.started_at).getTime()) / 1000;
      }
    }
    return any ? total : undefined;
  };

  const analysisStarted = status.status === 'analysing' || status.status === 'completed';
  const analysisDone = status.status === 'completed';
  const aiStatus: 'done' | 'running' | 'pending' =
    analysisDone ? 'done' : analysisStarted ? 'running' : 'pending';

  // Document Upload aggregates upload + virus_scan; we collapse virus_scan into "Upload" since
  // the team's design doesn't show it as a separate step.
  const uploadStatus = (() => {
    const a = allDocsDone('upload');
    const b = allDocsDone('virus_scan');
    if (a === 'done' && (b === 'done' || b === 'pending')) return 'done';
    if (a === 'running' || b === 'running') return 'running';
    if (a === 'done' || b === 'done') return 'running';
    return 'pending';
  })();

  return [
    {
      num: 1,
      name: 'Document Upload',
      subtitle: `${docCountStr}`,
      status: uploadStatus,
      durationSec: sumDuration('upload'),
    },
    {
      num: 2,
      name: 'Text Extraction',
      subtitle: 'OCR & layout analysis',
      status: allDocsDone('text_extraction'),
      durationSec: sumDuration('text_extraction'),
    },
    {
      num: 3,
      name: 'Embedding Generation',
      status: allDocsDone('embedding_generation'),
      durationSec: sumDuration('embedding_generation'),
    },
    {
      num: 4,
      name: 'AI Analysis',
      subtitle: 'Extract variables · check assumptions · verify calculations',
      status: aiStatus,
    },
    // The next two are cosmetic — they don't track real backend phases. They
    // flip done when AI Analysis flips done. See Issue 18 doubt 2 (option a).
    {
      num: 5,
      name: 'Benchmark Classification',
      subtitle: `Compare against ${BRAND_NAME} portfolio thresholds`,
      status: analysisDone ? 'done' : 'pending',
    },
    {
      num: 6,
      name: 'Report Generation',
      status: analysisDone ? 'done' : 'pending',
    },
  ];
}

function UnifiedPipeline({ status }: { status: AssessmentStatusResponse }) {
  const steps = buildPipeline(status);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const circleBg = s.status === 'done' ? 'var(--pass-bg)' : s.status === 'running' ? 'var(--module-soft)' : 'var(--surface-alt)';
        const circleBorder = s.status === 'done' ? 'var(--pass)' : s.status === 'running' ? 'var(--module-primary)' : 'var(--lines)';
        const circleColor = s.status === 'done' ? 'var(--pass)' : s.status === 'running' ? 'var(--module-primary)' : 'var(--muted)';
        return (
          <div key={s.num} style={{ display: 'flex', gap: 'var(--s3)', position: 'relative', paddingBottom: isLast ? 0 : 'var(--s4)' }}>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: circleBg, border: `2px solid ${circleBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: circleColor, fontSize: '0.75rem', fontWeight: 700,
              }}>
                {s.status === 'done' ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                ) : s.num}
              </div>
              {!isLast && (
                <div style={{
                  flex: 1, width: 2, marginTop: 4, marginBottom: 4,
                  background: s.status === 'done' ? 'var(--pass)' : 'var(--lines)',
                }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--s2)' }}>
                <span style={{ fontWeight: 600, color: s.status === 'pending' ? 'var(--muted)' : 'var(--text)', fontSize: '0.9375rem' }}>{s.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {s.durationSec != null && s.status === 'done' && (
                    <span className="t-xs t-muted">{formatDuration(s.durationSec)}</span>
                  )}
                  {s.status === 'done' && <span className="badge badge-pass" style={{ fontSize: '0.7rem' }}>Done</span>}
                  {s.status === 'running' && (
                    <span className="badge badge-info" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span className="spinner" style={{ width: 8, height: 8 }} />Running
                    </span>
                  )}
                </div>
              </div>
              {s.subtitle && (
                <p className="t-xs t-muted" style={{ margin: '2px 0 0' }}>{s.subtitle}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AssessmentPage() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  /* Sprint 5 — when the upload was project-scoped, the upload page
     forwards `?return_to=/projects/AUDIT-YYYY-NNNN` so the analyst can
     navigate back to the project workspace with one click. Parse the
     AUDIT-... id out and surface a Back-to-project banner; without it,
     fall back to the standalone breadcrumb. */
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');
  const projectAuditId = returnTo?.match(/^\/projects\/(AUDIT-[^/?#]+)/)?.[1] ?? null;
  const { status, isLoading, error } = useAssessmentStatus(assessmentId ?? '');
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [isSubmittingType, setIsSubmittingType] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // Re-render every second so Elapsed / Remaining countdown stays live.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status?.status === 'completed' || status?.status === 'failed' || status?.status === 'cancelled') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status?.status]);

  // Sprint 5 fix — terminal end-time used to be `Date.now()` snapped
  // when the FE first observed the terminal status. That broke when
  // an analyst reloaded a long-cancelled assessment: FE-now was hours
  // past the real backend cancellation → "Stopped after 335 min"
  // type inflation. Now we derive the end time from the most-recent
  // timestamp actually written by the backend — the max(completed_at)
  // across every document's processing_steps[]. Falls back to
  // Date.now() only when no steps have a timestamp (very-early
  // cancellation, before any worker work landed).
  const [terminalAt, setTerminalAt] = useState<number | null>(null);
  useEffect(() => {
    const terminal = status?.status === 'completed'
      || status?.status === 'failed'
      || status?.status === 'cancelled';
    if (terminal && terminalAt == null) {
      const stepTimes: number[] = [];
      for (const d of status?.documents ?? []) {
        for (const s of d.processing_steps ?? []) {
          if (s.completed_at) {
            stepTimes.push(new Date(s.completed_at).getTime());
          }
          if (s.started_at) {
            stepTimes.push(new Date(s.started_at).getTime());
          }
        }
      }
      setTerminalAt(stepTimes.length > 0 ? Math.max(...stepTimes) : Date.now());
    }
  }, [status?.status, terminalAt, status?.documents]);

  useEffect(() => {
    if (status?.status === 'completed' && assessmentId && !analysis) {
      getAssessmentAnalysis(assessmentId)
        .then((data) => {
          setAnalysis(data);
          // Check if any attribute needs user input for investment type
          const needsInput = data.attribute_results?.some((a) => a.needs_user_input);
          if (needsInput) {
            setShowInvestmentModal(true);
          }
        })
        .catch(() => null);
    }
  }, [status?.status, assessmentId, analysis]);

  async function handleCancel() {
    if (!assessmentId) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelAssessment(assessmentId);
      setShowCancelConfirm(false);
      // Status will update via WebSocket / next poll; no manual refresh needed.
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Cancel failed. Please try again.';
      setCancelError(typeof msg === 'string' ? msg : 'Cancel failed.');
    } finally {
      setIsCancelling(false);
    }
  }

  async function handleConfirmInvestmentType(key: string, label: string) {
    if (!assessmentId) return;
    setIsSubmittingType(true);
    try {
      await setInvestmentType(assessmentId, key, label);
      setShowInvestmentModal(false);
      setAnalysis(null); // will re-fetch when status goes back to completed
    } catch {
      // keep modal open on error
    } finally {
      setIsSubmittingType(false);
    }
  }

  if (!assessmentId) {
    return <div className="page-body"><div className="alert alert-fail">Invalid assessment ID</div></div>;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="page-body">
        <div className="alert alert-fail">{error ?? 'Assessment not found'}</div>
        <button className="btn btn-secondary" style={{ marginTop: 'var(--s3)' }} onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  const moduleClass = status.module_type === 'SPA' ? 'module-spa' : 'module-dd';
  const uploadPath = status.module_type === 'SPA' ? '/spa/upload' : '/dd/upload';
  const isCompleted = status.status === 'completed';
  const isAnalysing = status.status === 'analysing';
  const isFailed = status.status === 'failed';
  const isCancelled = status.status === 'cancelled';
  const isCancellable = status.status === 'ingesting' || status.status === 'analysing';
  const docsCompleted = status.documents.filter(d => d.status === 'completed').length;

  // Elapsed / Remaining (linear extrapolation — wildly off near start, OK after ~10% done).
  // End-of-run timestamp must be FIXED once the assessment hits a terminal
  // state so "Total time" doesn't keep growing every render (Date.now()
  // gets re-called whenever a status poll or WebSocket event triggers a
  // rerender, which would happen indefinitely after the page is mounted).
  const isTerminal = isCompleted || isFailed || isCancelled;
  const endMs = (() => {
    if (isCompleted && analysis?.analysis_completed_at) {
      return new Date(analysis.analysis_completed_at).getTime();
    }
    if (isTerminal && terminalAt != null) {
      return terminalAt;
    }
    return now;
  })();
  const elapsedSec = status.created_at
    ? Math.max(0, (endMs - new Date(status.created_at).getTime()) / 1000)
    : 0;
  // Sprint 5 client feedback 2026-07-03 — clamped pct was previously
  // used by the (now-removed) "Remaining" ETA. The visible progress
  // reads status.overall_progress_pct directly (see below) so this
  // intermediate no longer earns its keep.

  // Investment type options from analysis (if needs_user_input)
  const investmentTypeOptions = analysis?.attribute_results
    ?.find((a) => a.needs_user_input)
    ?.user_input_options ?? [];

  // Workflow indicator: Upload always done by now; Processing active until completed; Report done when completed
  const wiProcessing = isCompleted ? 'done' : 'active';
  const wiReport = isCompleted ? 'active' : 'pending';

  const subtypeShort: Record<string, string> = {
    InvestmentMemo: 'Investment Memo',
    FinancialDD: 'Financial DD',
    TechnicalDD: 'Technical DD',
    SPA: 'Share Purchase Agreement',
    SHA: 'Shareholders Agreement',
    Escrow: 'Escrow Agreement',
  };
  const primarySubtype = status.documents[0]?.document_subtype ?? null;

  return (
    <div className={moduleClass}>
      {showInvestmentModal && investmentTypeOptions.length > 0 && (
        <InvestmentTypeModal
          assessmentId={assessmentId}
          options={investmentTypeOptions}
          onConfirm={handleConfirmInvestmentType}
          onClose={() => setShowInvestmentModal(false)}
          isSubmitting={isSubmittingType}
        />
      )}

      {showCancelConfirm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !isCancelling && setShowCancelConfirm(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 460, width: '90%', margin: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header">
              <h2 className="t-h3">Cancel this assessment?</h2>
            </div>
            <div style={{ padding: 'var(--s3) var(--s4)' }}>
              <p className="t-sm" style={{ marginTop: 0 }}>
                The running job for <strong>{status.deal_name ?? assessmentId}</strong> will be
                terminated. Any extracted attributes already saved will remain,
                but the analysis will be marked <strong>cancelled</strong>.
              </p>
              <p className="t-xs t-muted">This cannot be undone — you'll need to re-trigger analysis if you want to resume.</p>
              {cancelError && (
                <div className="alert alert-fail" style={{ marginTop: 'var(--s3)' }}>
                  {cancelError}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 'var(--s2)', padding: '0 var(--s4) var(--s4)', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCancelConfirm(false)}
                disabled={isCancelling}
                type="button"
              >
                Keep running
              </button>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--fail)', borderColor: 'var(--fail)' }}
                onClick={handleCancel}
                disabled={isCancelling}
                type="button"
              >
                {isCancelling ? <><span className="spinner" /> Cancelling…</> : 'Cancel Job'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="module-page-header">
        {projectAuditId && (
          <div
            style={{
              padding: 'var(--s3) var(--s4)',
              background:
                status.module_type === 'SPA'
                  ? 'var(--spa-accent-bg)'
                  : 'var(--dd-accent-bg)',
              border:
                status.module_type === 'SPA'
                  ? '1px solid var(--spa-accent-border)'
                  : '1px solid var(--dd-accent-border)',
              borderRadius: 'var(--r-md)',
              marginBottom: 'var(--s4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '0.9rem' }}>
              Processing Stage{' '}
              {status.module_type === 'SPA' ? '2 (SPA)' : '1 (DD)'} for
              project{' '}
              <strong style={{ fontFamily: 'var(--font-mono)' }}>
                {projectAuditId}
              </strong>
              {isCompleted && (
                <span style={{ marginLeft: 'var(--s3)', color: 'var(--pass)' }}>
                  · Complete
                </span>
              )}
              {isFailed && (
                <span style={{ marginLeft: 'var(--s3)', color: 'var(--fail)' }}>
                  · Failed
                </span>
              )}
            </span>
            <button
              className={
                isCompleted ? 'btn btn-primary' : 'btn btn-ghost btn-sm'
              }
              onClick={() => navigate(returnTo || `/projects/${projectAuditId}`)}
            >
              ← Back to project
            </button>
          </div>
        )}
        <div className="breadcrumb">
          {projectAuditId ? (
            <>
              <a href="/projects" onClick={(e) => { e.preventDefault(); navigate('/projects'); }}>Projects</a>
              <span className="bc-sep">/</span>
              <a href={`/projects/${projectAuditId}`} onClick={(e) => { e.preventDefault(); navigate(`/projects/${projectAuditId}`); }}>{projectAuditId}</a>
              <span className="bc-sep">/</span>
              <span>Stage {status.module_type === 'SPA' ? '2 (SPA)' : '1 (DD)'} · Processing</span>
            </>
          ) : (
            <>
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Dashboard</a>
              <span className="bc-sep">/</span>
              <a href={uploadPath} onClick={(e) => { e.preventDefault(); navigate(uploadPath); }}>{status.module_type} Assessments</a>
              <span className="bc-sep">/</span>
              <span>Processing</span>
            </>
          )}
        </div>
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">
              {status.module_type} Module
              {primarySubtype ? ` · ${subtypeShort[primarySubtype] ?? primarySubtype}` : ''}
            </p>
            <h1 className="t-h1">
              {status.deal_name ? status.deal_name : `Assessment — ${status.documents.length} Document${status.documents.length !== 1 ? 's' : ''}`}
            </h1>
            <p className="t-xs t-muted" style={{ marginTop: 4 }}>{assessmentId}</p>
          </div>
          <div className="workflow-indicator">
            <div className={`wi-step done`}><div className="wi-num">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
            </div><span>Upload</span></div>
            <div className={`wi-line${wiProcessing === 'done' ? ' done' : ''}`} />
            <div className={`wi-step ${wiProcessing}`}><div className="wi-num">
              {wiProcessing === 'done' ? (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              ) : '2'}
            </div><span>Processing</span></div>
            <div className={`wi-line${wiReport === 'active' ? ' done' : ''}`} />
            <div className={`wi-step ${wiReport}`}><div className="wi-num">3</div><span>Report</span></div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="proc-layout">
          {/* LEFT COLUMN */}
          <div className="proc-main">
            {/* Overall progress — 2-column / 3-row design (see Issue 27 mock):
                  Row 1: label (top-left)        progress bar (top-right)
                  Row 2: big % (middle-left)     status text + CTA (middle-right)
                  Row 3: Elapsed | Remaining (spans both columns, footer) */}
            <div className="card overall-progress-card">
              <div className="op-grid">
                <p className="t-label op-label">Overall Progress</p>
                <div className="op-bar">
                  <div className="progress-bar">
                    <div
                      className={`progress-fill${(status.status === 'ingesting' || isAnalysing) ? ' shimmer' : ''}`}
                      style={{ width: `${status.overall_progress_pct}%` }}
                    />
                  </div>
                </div>

                <div className="op-pct">
                  <span style={{ fontSize: '4rem', fontWeight: 600, lineHeight: 1, fontFamily: 'var(--font-display)' }}>{status.overall_progress_pct}%</span>
                </div>
                <div className="op-status">
                  {isCompleted && (
                    <span className="t-sm" style={{ color: 'var(--muted)' }}>
                      Processing complete.{' '}
                      <a
                        href={`/reports/${assessmentId}`}
                        onClick={(e) => { e.preventDefault(); navigate(`/reports/${assessmentId}`); }}
                        style={{ color: 'var(--module-primary)', fontWeight: 600 }}
                      >
                        View report now →
                      </a>
                    </span>
                  )}
                  {isAnalysing && (
                    <span className="t-sm t-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="spinner" style={{ width: 10, height: 10 }} />
                      Auto-redirect on completion
                    </span>
                  )}
                  {!isCompleted && !isAnalysing && (
                    <span className="t-sm t-muted">{docsCompleted}/{status.documents.length} documents ingested</span>
                  )}
                </div>

                {status.created_at && (
                  <div className="op-footer">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--muted)' }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {isCompleted ? (
                      <span><span className="t-muted">Total time: </span><strong>{formatDuration(elapsedSec)}</strong></span>
                    ) : isFailed || isCancelled ? (
                      <span><span className="t-muted">Stopped after: </span><strong>{formatDuration(elapsedSec)}</strong></span>
                    ) : (
                      // Sprint 5 client feedback 2026-07-03 — removed the
                      // in-progress "Remaining: ~Xm Ys" ETA. The estimator
                      // was linear extrapolation from elapsed/pct which
                      // misrepresents non-linear stages (embedding vs
                      // LLM-extraction vs Tavily lookups have very
                      // different per-percent costs), and users flagged
                      // it as noisy. Keep only the deterministic
                      // "Elapsed" clock.
                      <span><span className="t-muted">Elapsed: </span><strong>{formatDuration(elapsedSec)}</strong></span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Failed banner */}
            {isFailed && (
              <div className="alert alert-fail">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Processing failed. Please try uploading the documents again.
              </div>
            )}

            {/* Cancelled banner */}
            {isCancelled && (
              <div className="alert alert-warn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                Assessment cancelled. Any partial results above are preserved.
              </div>
            )}

            {/* Investment type needed banner */}
            {isCompleted && analysis && investmentTypeOptions.length > 0 && !showInvestmentModal && (
              <div className="alert alert-warn">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <div style={{ flex: 1 }}>
                  <strong>Investment Type not confirmed.</strong> Benchmark comparison pending — analysis partially complete.
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setShowInvestmentModal(true)} type="button">
                  Select Investment Type
                </button>
              </div>
            )}

            {/* Unified cross-document pipeline */}
            <div className="card">
              <div className="card-header">
                <h2 className="t-h3">Processing Steps</h2>
                <span className={`badge ${isAnalysing ? 'badge-info' : isCompleted ? 'badge-pass' : 'badge-neutral'}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {isAnalysing && <span className="spinner" style={{ width: 8, height: 8 }} />}
                  {isAnalysing ? 'Running' : isCompleted ? 'Completed' : isFailed ? 'Failed' : isCancelled ? 'Cancelled' : 'Pending'}
                </span>
              </div>
              <UnifiedPipeline status={status} />
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="proc-sidebar">
            <div className="card card-sm">
              <p className="t-label t-label-mb3">Files being processed</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s3)' }}>
                {status.documents.map((doc) => {
                  const ext = (doc.filename.split('.').pop() ?? '').toUpperCase();
                  return (
                    <div key={doc.job_id} style={{ display: 'flex', gap: 'var(--s3)', alignItems: 'flex-start' }}>
                      <span className="file-item-icon" style={{ fontSize: '10px', padding: '3px 5px', minWidth: 30, textAlign: 'center', flexShrink: 0 }}>{ext || '—'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="t-sm" style={{ fontWeight: 500, wordBreak: 'break-word', lineHeight: 1.3 }}>{doc.filename}</div>
                        <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                          {subtypeShort[doc.document_subtype ?? ''] ?? doc.document_subtype ?? '—'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card card-sm">
              <p className="t-label t-label-mb3">Configuration</p>
              <div className="meta-grid">
                <div className="meta-row">
                  <span className="t-muted t-sm">Type</span>
                  <span className="t-sm">{status.module_type === 'SPA' ? 'Share Purchase Agreement (SPA)' : 'Due Diligence (DD)'}</span>
                </div>
                <div className="meta-row">
                  <span className="t-muted t-sm">Benchmark</span>
                  <span className="t-sm t-mono" style={{ color: 'var(--module-primary)' }}>
                    {analysis?.investment_type?.label
                      ? `${BRAND_CODE}-${(analysis.investment_type.key ?? '').toUpperCase()}`
                      : <span className="t-muted">Pending Investment Type</span>}
                  </span>
                </div>
                {status.analyst_name && (
                  <div className="meta-row">
                    <span className="t-muted t-sm">Analyst</span>
                    <span className="t-sm">{status.analyst_name}</span>
                  </div>
                )}
                <div className="meta-row">
                  <span className="t-muted t-sm">Job ID</span>
                  <span className="t-sm t-mono">{assessmentId}</span>
                </div>
                {status.created_at && (
                  <div className="meta-row">
                    <span className="t-muted t-sm">Submitted</span>
                    <span className="t-sm">{new Date(status.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="card card-sm">
              <p className="t-label t-label-mb3">Actions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => navigate(`/reports/${assessmentId}`)}
                  disabled={!isCompleted}
                  type="button"
                >
                  View Report
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
                {isCancellable && (
                  <button
                    className="btn btn-secondary btn-full"
                    style={{ color: 'var(--fail)', borderColor: 'var(--fail-border)' }}
                    onClick={() => { setCancelError(null); setShowCancelConfirm(true); }}
                    type="button"
                  >
                    Cancel Job
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
